import { useApi } from '@mainstage/core-plugin-api';
import { ServiceAccountWatchEvent } from '@agilelab/plugin-wb-service-accounts-common';
import { Observable } from '@mainstage/types';
import ObservableImpl from 'zen-observable';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { discoveryApiRef, identityApiRef } from '@mainstage/core-plugin-api';

export interface WatchServiceAccountParams {
  ref: string;
  after?: number;
  timeout?: number;
}

export const useWatchServiceAccount = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const watchServiceAccount = ({
    ref,
    after = Date.now(),
    timeout,
  }: WatchServiceAccountParams): Observable<ServiceAccountWatchEvent> => {
    return new ObservableImpl(subscriber => {
      const params = new URLSearchParams();
      params.set('after', String(Number(after)));
      if (timeout !== undefined) {
        params.set('timeout', String(timeout));
      }

      let closed = false;
      let eventSource: EventSourcePolyfill | undefined;

      Promise.all([
        discoveryApi.getBaseUrl('catalog'),
        identityApi.getCredentials(),
      ]).then(
        ([baseUrl, credentials]) => {
          if (closed) {
            return;
          }

          params.set('ref', ref);
          const url = `${baseUrl}/service-accounts/watch?${params.toString()}`;

          const headers: Record<string, string> = {};
          if (credentials?.token) {
            headers.Authorization = `Bearer ${credentials.token}`;
          }

          eventSource = new EventSourcePolyfill(url, {
            withCredentials: true,
            headers,
          });

          const processEvent = (event: any) => {
            if (event.data) {
              try {
                const data = JSON.parse(event.data) as ServiceAccountWatchEvent;
                subscriber.next(data);
                if (
                  data.status === 'found' ||
                  data.status === 'timeout' ||
                  data.status === 'invalid-entity'
                ) {
                  eventSource?.close();
                  subscriber.complete();
                }
              } catch (ex) {
                eventSource?.close();
                subscriber.error(ex);
              }
            }
          };

          eventSource.addEventListener('status', processEvent);
          eventSource.addEventListener('error', event => {
            eventSource?.close();
            subscriber.error(event);
          });
        },
        error => {
          subscriber.error(error);
        },
      );

      return () => {
        closed = true;
        eventSource?.close();
      };
    });
  };

  return { watchServiceAccount };
};
